import { Actor, CollisionType, Color, Circle, Engine, vec, Vector, Shape, Animation } from "excalibur";
import { VIRUS, VISUALS, ENEMIES } from "../config";
import { Macrophage } from "./Macrophage";
import { VirusCollision } from "../systems/CollisionGroups";
import { Resources } from "../resources";
import { createPackedAnimation } from "../utils/AtlasLoader";
import { AudioManager } from "../systems/AudioManager";

/**
 * Enemy type enumeration based on the biochemical metaphors
 */
export enum EnemyType {
    /** Basic red enemy - balanced stats */
    INFLUENZA = 'INFLUENZA',
    /** Green swarmer - fast, weak, light (high knockback) */
    RHINOVIRUS = 'RHINOVIRUS',
    /** Purple tank - slow, tough, heavy (low knockback) */
    ADENOVIRUS = 'ADENOVIRUS',
    /** Blue boss - spawns children, variable movement */
    MUTATED_STRAIN = 'MUTATED_STRAIN',
}

/**
 * Enemy stats interface for type-specific configuration
 */
interface EnemyStats {
    speed: number;
    hp: number;
    damage: number;
    radius: number;
    mass: number;
    xpValue: number;
    color: string;
}

/**
 * Get stats for an enemy type
 */
function getEnemyStats(type: EnemyType): EnemyStats {
    switch (type) {
        case EnemyType.RHINOVIRUS:
            return {
                speed: ENEMIES.RHINOVIRUS.SPEED,
                hp: ENEMIES.RHINOVIRUS.HP,
                damage: ENEMIES.RHINOVIRUS.DAMAGE,
                radius: ENEMIES.RHINOVIRUS.RADIUS,
                mass: ENEMIES.RHINOVIRUS.MASS,
                xpValue: ENEMIES.RHINOVIRUS.XP_VALUE,
                color: ENEMIES.RHINOVIRUS.COLOR,
            };
        case EnemyType.ADENOVIRUS:
            return {
                speed: ENEMIES.ADENOVIRUS.SPEED,
                hp: ENEMIES.ADENOVIRUS.HP,
                damage: ENEMIES.ADENOVIRUS.DAMAGE,
                radius: ENEMIES.ADENOVIRUS.RADIUS,
                mass: ENEMIES.ADENOVIRUS.MASS,
                xpValue: ENEMIES.ADENOVIRUS.XP_VALUE,
                color: ENEMIES.ADENOVIRUS.COLOR,
            };
        case EnemyType.MUTATED_STRAIN:
            return {
                speed: ENEMIES.MUTATED_STRAIN.SPEED,
                hp: ENEMIES.MUTATED_STRAIN.HP,
                damage: ENEMIES.MUTATED_STRAIN.DAMAGE,
                radius: ENEMIES.MUTATED_STRAIN.RADIUS,
                mass: ENEMIES.MUTATED_STRAIN.MASS,
                xpValue: ENEMIES.MUTATED_STRAIN.XP_VALUE,
                color: ENEMIES.MUTATED_STRAIN.COLOR,
            };
        case EnemyType.INFLUENZA:
        default:
            return {
                speed: VIRUS.BASE_SPEED,
                hp: 10,
                damage: VIRUS.DAMAGE,
                radius: VIRUS.RADIUS,
                mass: 3,
                xpValue: VIRUS.XP_VALUE,
                color: VISUALS.COLORS.VIRUS_CAPSID,
            };
    }
}

/**
 * Cached animations for each enemy type (shared across all instances)
 * We also store the original width before any scaling
 */
const animationCache: Map<EnemyType, Animation> = new Map();
const animationOriginalWidths: Map<EnemyType, number> = new Map();

/**
 * Initialize animations for all enemy types
 * Call this once after resources are loaded
 */
function loadEnemyAnimations(): void {
    if (animationCache.size > 0) return; // Already loaded

    try {
        // Influenza animation
        const influenzaAnim = createPackedAnimation(
            Resources.InfluenzaImage,
            Resources.InfluenzaJson,
            10
        );
        animationCache.set(EnemyType.INFLUENZA, influenzaAnim);
        animationOriginalWidths.set(EnemyType.INFLUENZA, influenzaAnim.width);

        // Rhinovirus animation
        const rhinoAnim = createPackedAnimation(
            Resources.RhinovirusImage,
            Resources.RhinovirusJson,
            10
        );
        animationCache.set(EnemyType.RHINOVIRUS, rhinoAnim);
        animationOriginalWidths.set(EnemyType.RHINOVIRUS, rhinoAnim.width);

        // Adenovirus animation
        const adenoAnim = createPackedAnimation(
            Resources.AdenovirusImage,
            Resources.AdenovirusJson,
            8 // Slower, tankier enemy
        );
        animationCache.set(EnemyType.ADENOVIRUS, adenoAnim);
        animationOriginalWidths.set(EnemyType.ADENOVIRUS, adenoAnim.width);

        // Mutated Strain animation
        const mutatedAnim = createPackedAnimation(
            Resources.MutatedStrainImage,
            Resources.MutatedStrainJson,
            6 // Boss pulsing effect
        );
        animationCache.set(EnemyType.MUTATED_STRAIN, mutatedAnim);
        animationOriginalWidths.set(EnemyType.MUTATED_STRAIN, mutatedAnim.width);
    } catch (error) {
        console.warn('Failed to load enemy animations', error);
    }
}

/**
 * Get animation for an enemy type from cache (shared, no cloning to avoid memory leaks)
 * Each virus instance will store its own animation to avoid repeated cloning
 */
function getCachedAnimation(type: EnemyType): Animation | null {
    // Ensure animations are loaded
    if (animationCache.size === 0) {
        loadEnemyAnimations();
    }
    return animationCache.get(type) ?? null;
}

/**
 * Get the original (unscaled) width of an animation type
 */
function getOriginalWidth(type: EnemyType): number {
    return animationOriginalWidths.get(type) ?? 64; // Default to 64 if not found
}

/**
 * Callback for Mutated Strain spawning children
 */
export type SpawnCallback = (x: number, y: number) => void;

/**
 * Virus - The Enemy
 * 
 * Supports multiple enemy types with different stats and behaviors:
 * - Influenza (red): Basic seeker
 * - Rhinovirus (green): Fast swarmer, light mass
 * - Adenovirus (purple): Armored tank, heavy mass
 * - Mutated Strain (blue): Spawner boss
 * 
 * These are pooled objects - they don't get destroyed, just deactivated and reused.
 * MEMORY OPTIMIZATION: Each instance stores its own animations to avoid constant cloning.
 */
export class Virus extends Actor {
    private target: Macrophage | null = null;
    private speed: number = VIRUS.BASE_SPEED;
    public speedModifier: number = 1.0;
    private poolActive: boolean = false;
    private enemyType: EnemyType = EnemyType.INFLUENZA;

    // ... (rest of props)
    // HP system for tankier enemies
    private currentHp: number = 10;
    private currentDamage: number = VIRUS.DAMAGE;
    private currentXpValue: number = VIRUS.XP_VALUE;

    // Mutated Strain specific
    private spawnTimer: number = 0;
    private spawnCallback: SpawnCallback | null = null;

    // Burst movement for Mutated Strain
    private burstTimer: number = 0;
    private isBursting: boolean = false;

    // Per-instance animation cache (created once, reused for life of pool object)
    // Key difference: we now store the original width alongside to avoid scale accumulation
    private instanceAnimationCache: Map<EnemyType, { animation: Animation; originalWidth: number }> = new Map();
    private fallbackGraphicCache: Map<EnemyType, Circle> = new Map();

    constructor() {
        super({
            name: 'Virus',
            radius: VIRUS.RADIUS,
            color: Color.fromHex(VISUALS.COLORS.VIRUS_CAPSID),
            collisionType: CollisionType.Active,
            collisionGroup: VirusCollision,
        });
    }

    override onInitialize(engine: Engine): void {
        // Default collider - will be updated on activate
        this.setupCollider(VIRUS.RADIUS);
        this.updateGraphics(EnemyType.INFLUENZA, VIRUS.RADIUS);
    }

    private setupCollider(radius: number): void {
        const collider = Shape.Circle(radius) as any;
        collider.group = VirusCollision;
        this.collider.set(collider);
    }

    private updateGraphics(type: EnemyType, radius: number): void {
        // Check if we have a cached animation for this type
        let cached = this.instanceAnimationCache.get(type);

        if (!cached) {
            // Clone from the global cache only once per type per instance
            const baseAnim = getCachedAnimation(type);
            if (baseAnim) {
                const animation = baseAnim.clone();
                const originalWidth = getOriginalWidth(type);
                cached = { animation, originalWidth };
                this.instanceAnimationCache.set(type, cached);
            }
        }

        if (cached) {
            // Calculate scale based on ORIGINAL width (not current scaled width)
            // This prevents scale accumulation bugs
            const targetSize = radius * 2 * VISUALS.CONTRAST.ENEMY_SCALE;
            const scale = targetSize / cached.originalWidth;
            cached.animation.scale = vec(scale, scale);
            cached.animation.opacity = VISUALS.CONTRAST.ENEMY_BRIGHTNESS;
            this.graphics.use(cached.animation);
        } else {
            // Fallback to circle graphic - cache these too
            let fallbackGraphic = this.fallbackGraphicCache.get(type);
            if (!fallbackGraphic) {
                const stats = getEnemyStats(type);
                fallbackGraphic = new Circle({
                    radius: radius,
                    color: Color.fromHex(stats.color),
                    strokeColor: Color.fromHex(stats.color).darken(0.3),
                    lineWidth: 3,
                });
                this.fallbackGraphicCache.set(type, fallbackGraphic);
            }
            this.graphics.use(fallbackGraphic);
        }
    }

    override onPreUpdate(engine: Engine, delta: number): void {
        // Reset speed modifier every frame (modifiers must be re-applied constantly)
        this.speedModifier = 1.0;

        if (!this.poolActive || !this.target) return;

        // Mutated Strain special behavior
        if (this.enemyType === EnemyType.MUTATED_STRAIN) {
            this.handleMutatedStrainBehavior(delta);
        } else {
            // Normal chase behavior
            this.chaseTarget();
        }
    }

    /**
     * Apply a slow effect (must be called every frame)
     */
    public applySlow(factor: number): void {
        this.speedModifier = Math.min(this.speedModifier, factor);
    }

    private chaseTarget(): void {
        if (!this.target) return;

        const direction = this.target.pos.sub(this.pos);
        if (direction.size > 1) {
            // Apply speed modifier
            const currentSpeed = this.speed * this.speedModifier;
            this.vel = direction.normalize().scale(currentSpeed);
        } else {
            this.vel = Vector.Zero;
        }
    }

    private handleMutatedStrainBehavior(delta: number): void {
        // Spawn timer
        this.spawnTimer += delta;
        if (this.spawnTimer >= ENEMIES.MUTATED_STRAIN.SPAWN_COOLDOWN) {
            this.spawnTimer = 0;
            this.spawnChildren();
        }

        // Burst movement
        this.burstTimer += delta;
        if (this.isBursting) {
            // Move fast during burst
            this.chaseTarget();
            if (this.burstTimer >= 500) { // Burst for 500ms
                this.isBursting = false;
                this.burstTimer = 0;
                this.speed = 0; // Stop
                this.vel = Vector.Zero;
            }
        } else {
            // Pause between bursts
            this.vel = Vector.Zero;
            if (this.burstTimer >= 2000) { // Pause for 2 seconds
                this.isBursting = true;
                this.burstTimer = 0;
                this.speed = 200; // Fast burst speed
            }
        }
    }

    private spawnChildren(): void {
        if (this.spawnCallback) {
            // Spawn 2 Rhinoviruses at current position
            const offset1 = vec(30, 0).rotate(Math.random() * Math.PI * 2);
            const offset2 = vec(30, 0).rotate(Math.random() * Math.PI * 2);

            this.spawnCallback(this.pos.x + offset1.x, this.pos.y + offset1.y);
            this.spawnCallback(this.pos.x + offset2.x, this.pos.y + offset2.y);
        }
    }

    /**
     * Activate this virus from the pool
     */
    public activate(
        x: number,
        y: number,
        target: Macrophage,
        type: EnemyType = EnemyType.INFLUENZA,
        speedOverride?: number,
        spawnCallback?: SpawnCallback
    ): void {
        this.pos = vec(x, y);
        this.target = target;
        this.enemyType = type;
        this.poolActive = true;
        this.graphics.visible = true;
        this.graphics.opacity = 1; // Ensure full opacity on activation
        this.body.collisionType = CollisionType.Active;

        // Get stats for this enemy type
        const stats = getEnemyStats(type);
        this.speed = speedOverride ?? stats.speed;
        this.currentHp = stats.hp;
        this.currentDamage = stats.damage;
        this.currentXpValue = stats.xpValue;

        // Update collider and graphics for this type
        this.setupCollider(stats.radius);
        this.updateGraphics(type, stats.radius);

        // Reset Mutated Strain timers
        this.spawnTimer = 0;
        this.burstTimer = 0;
        this.isBursting = false;
        this.spawnCallback = spawnCallback ?? null;

        // Set physics mass
        this.body.mass = stats.mass;
    }

    /**
     * Return this virus to the pool
     */
    public deactivate(): void {
        this.poolActive = false;
        this.target = null;
        this.vel = Vector.Zero;
        this.pos = vec(-1000, -1000);
        this.graphics.visible = false;
        this.body.collisionType = CollisionType.PreventCollision;
        this.spawnCallback = null;
    }

    /**
     * Check if this virus is active in the pool
     */
    public isPoolActive(): boolean {
        return this.poolActive;
    }

    /**
     * Take damage and return true if killed
     */
    public takeDamage(amount: number): boolean {
        this.currentHp -= amount;

        // Play hit sound
        AudioManager.playHitSound();

        // Flash effect on hit
        this.graphics.opacity = 0.5;
        this.actions.delay(100).callMethod(() => {
            if (this.poolActive) {
                this.graphics.opacity = 1;
            }
        });

        return this.currentHp <= 0;
    }

    /**
     * Get the damage this virus deals
     */
    public getDamage(): number {
        return this.currentDamage;
    }

    /**
     * Override kill to work with Object Pool
     * prevent removing from scene, just deactivate
     */
    public override kill(): void {
        this.deactivate();
    }

    /**
     * Get XP value for killing this virus
     */
    public getXPValue(): number {
        return this.currentXpValue;
    }

    /**
     * Get the enemy type
     */
    public getEnemyType(): EnemyType {
        return this.enemyType;
    }
}
